import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const TOKEN_KEY = "caireen.admin.token";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [token, setTokenState] = useState(() => {
        try {
            return window.localStorage.getItem(TOKEN_KEY) || null;
        } catch {
            return null;
        }
    });
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const setToken = useCallback((next) => {
        setTokenState(next);
        try {
            if (next) window.localStorage.setItem(TOKEN_KEY, next);
            else window.localStorage.removeItem(TOKEN_KEY);
        } catch {
            /* ignore */
        }
    }, []);

    const fetchMe = useCallback(async (t) => {
        if (!t) {
            setUser(null);
            return null;
        }
        try {
            const { data } = await axios.get(`${API_BASE}/auth/me`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            setUser(data);
            return data;
        } catch (e) {
            setUser(null);
            setToken(null);
            return null;
        }
    }, [setToken]);

    useEffect(() => {
        (async () => {
            await fetchMe(token);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await axios.post(`${API_BASE}/auth/login`, { email, password });
        setToken(data.token);
        setUser(data.user);
        return data.user;
    }, [setToken]);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
    }, [setToken]);

    const authHeader = useCallback(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    return (
        <AuthContext.Provider
            value={{ token, user, loading, login, logout, authHeader, API_BASE }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
